import React, { useContext, useMemo } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import Box from '@node-space/storybook-components/dist/Box'
import Callout from '@node-space/storybook-components/dist/Callout'
import Icon from '@node-space/storybook-components/dist/Icon'
import Text from '@node-space/storybook-components/dist/Text'
import { Button } from '@node-space/ui-components'
import { PathNames } from 'constants/General'
import { useWalletsQueryV2 } from 'hooks/queries/useWalletsQueryV2'
import { MovePayload } from 'pages/Customers/types'
import { WalletActionContext } from 'pages/Wallets/context/WalletSheetProvider'
import { WalletType } from 'types/types'
import { WalletTransferType } from 'types/unifiedPayouts/unifiedPayouts'
import { WalletTypes } from 'types/wallets'
import { roundNumberWithCommas } from 'utils/utils'
import { useSourceWallet } from './hooks/useSourceWallet'

interface MoveCompleteProps {
  formValues?: MovePayload
  transferError?: string
}

export const MoveComplete = ({ formValues, transferError }: MoveCompleteProps) => {
  const { t } = useTranslation()
  const sourceWallet = useSourceWallet()
  const navigate = useNavigate()
  const [walletAction] = useContext(WalletActionContext)
  const queryClient = useQueryClient()
  const isCustomerWalletTransfer = formValues?.walletTransferType === WalletTransferType.CUSTOMER
  const isTransferFromCustomer =
    walletAction?.meta?.sourceWalletType === WalletTransferType?.CUSTOMER

  const { data: wallets, isLoading: isLoadingWallets } = useWalletsQueryV2({
    walletType: WalletTypes.FIAT,
    searchTerm: sourceWallet?.currency,
    max: 1000,
  })

  const destinationMainWallet = wallets?.data?.find((wallet: WalletType) => {
    return wallet?.lsid === formValues?.destinationWallet
  })

  const goToTransferDestination = () => {
    if (!isCustomerWalletTransfer && destinationMainWallet) {
      queryClient.invalidateQueries({ queryKey: ['wallet'] })
      navigate(
        `${PathNames.WALLETS}/wallet-details/${destinationMainWallet?.id}?tab=recent-activity`,
        {
          replace: true,
        }
      )
    } else if (isCustomerWalletTransfer && isTransferFromCustomer) {
      queryClient.invalidateQueries({ queryKey: ['customerWallets'] })
      walletAction?.onClose?.()
    } else {
      queryClient.invalidateQueries({ queryKey: ['customerWallets'] })
      navigate(`${PathNames.CUSTOMERS}/${formValues?.customerReference}`, {
        replace: true,
      })
    }
  }

  const labels = useMemo(() => {
    const destinationLabel = t('customers.moveSuccessful', {
      amount: roundNumberWithCommas(formValues?.amount),
      currency: sourceWallet?.currency,
      customer: isCustomerWalletTransfer
        ? formValues?.destinationCustomerName
        : formValues?.destinationWalletName,
    })

    const customerWalletLabel = isCustomerWalletTransfer ? formValues?.destinationWalletName : ''

    return {
      destinationLabel,
      customerWalletLabel,
    }
  }, [formValues])

  const moveCompleteButtonLabel = isCustomerWalletTransfer
    ? t('customers.goToCustomer')
    : t('customers.goToWallet')

  return (
    <Box flex direction="col" alignItems="center" className="my-6 w-full space-y-4 text-gray-100">
      <Icon name="ClapIcon" color="status-success-500" size="lg" />
      <Text className="m-0" size="3xl" weight="semibold" align="center">
        {labels?.destinationLabel}
      </Text>
      {labels?.customerWalletLabel && (
        <Text className="m-0" size="3xl" weight="semibold" align="center">
          {t('customers.customerWallet')}: {formValues?.destinationWalletName}
        </Text>
      )}
      <Button variant="primary" onClick={goToTransferDestination} loading={isLoadingWallets}>
        {moveCompleteButtonLabel}
      </Button>
      {!!transferError && <Callout state="error" message={transferError} showCloseButton={false} />}
    </Box>
  )
}
